import { newId } from '../lib/id';
import * as db from './db';
import type { StoreName } from './db';
import { ApiError, api, apiUrl, syncConfigured } from './api';

export { syncConfigured } from './api';

/**
 * Getting a member's records to and from the server.
 *
 * Every record is queued here, private ones included, because the server is
 * also the member's backup and the only place their history survives losing
 * a phone. `visibility` decides who else can see it, not whether it is
 * stored. The outbox flushes when there is a server, a signal and a
 * session; otherwise it waits, which is the normal state on the water.
 */

export interface OutboxItem {
  id: string;
  createdAt: string;
  store: StoreName;
  op: 'upsert' | 'delete';
  /** id of the record in `store` */
  recordId: string;
  attempts: number;
  lastError?: string;
  /**
   * New on every enqueue. The flush only clears or updates the row it
   * actually sent; if the stamp has moved, the member changed the record
   * mid-upload and the newer row stands.
   */
  stamp?: string;
}

export async function enqueue(item: {
  store: StoreName;
  op: OutboxItem['op'];
  id: string;
}): Promise<void> {
  // Collapse repeated edits of the same record into one outbox row.
  const existing = (await db.getAll<OutboxItem>('outbox')).find(
    (row) => row.store === item.store && row.recordId === item.id,
  );
  await db.put<OutboxItem>('outbox', {
    id: existing?.id ?? newId(),
    createdAt: existing?.createdAt ?? new Date().toISOString(),
    store: item.store,
    op: item.op,
    recordId: item.id,
    attempts: 0,
    stamp: newId(),
  });
  sendSoon();
}

// ---- syncing as you go ----

/**
 * Off until the app starts it, so the data-layer tests, which drive the
 * outbox by hand, never race a flush they did not ask for.
 */
let live = false;
let sendTimer: ReturnType<typeof setTimeout> | undefined;
let lastExchange = 0;

/** A burst of edits, say a title and then the notes, goes as one upload. */
const SEND_AFTER_MS = 1500;
/** How often an open, visible app checks for other devices' changes. */
const CHECK_EVERY_MS = 2 * 60_000;
/** Coming back to the app within this of the last exchange does nothing. */
const RECHECK_AFTER_MS = 20_000;

/**
 * Sends what was just saved, shortly after, rather than waiting for the
 * app to be reopened. It used to wait, so an edit made with full signal
 * sat on the phone until the next launch, reconnect or "Sync now".
 */
function sendSoon(): void {
  if (!live) return;
  clearTimeout(sendTimer);
  sendTimer = setTimeout(() => {
    void flushOutbox();
  }, SEND_AFTER_MS);
}

/** Up, then down: anything written here reaches the server first. */
async function exchange(): Promise<void> {
  lastExchange = Date.now();
  const result = await flushOutbox();
  if (result.skipped) return;
  const { pullRecords } = await import('./pull');
  await pullRecords();
}

export function pendingCount(): Promise<number> {
  return db.getAll<OutboxItem>('outbox').then((rows) => rows.length);
}

export interface FlushResult {
  sent: number;
  failed: number;
  skipped: 'no-server' | 'offline' | 'signed-out' | 'needs-plan' | null;
  /** Set when the server refused the whole flush, for the app to show. */
  reason?: string;
}

let flushing: Promise<FlushResult> | null = null;

export function flushOutbox(): Promise<FlushResult> {
  // Reconnect events and manual taps can overlap; run one flush at a time.
  if (!flushing) {
    flushing = doFlush().finally(() => {
      flushing = null;
    });
  }
  return flushing;
}

async function doFlush(): Promise<FlushResult> {
  if (!syncConfigured) return { sent: 0, failed: 0, skipped: 'no-server' };
  if (!navigator.onLine) return { sent: 0, failed: 0, skipped: 'offline' };

  const rows = await db.getAll<OutboxItem>('outbox');
  let sent = 0;
  let failed = 0;

  for (const row of rows) {
    try {
      await send(row);
      // Only clear the row this flush sent. An edit or delete queued while
      // the request was out replaced it, and that newer row must survive.
      await db.modify<OutboxItem>('outbox', row.id, (current) =>
        current?.stamp === row.stamp ? null : undefined,
      );
      sent += 1;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Not signed in. Leave the queue untouched and stop trying.
        return { sent, failed, skipped: 'signed-out' };
      }
      if (err instanceof ApiError && err.status === 402) {
        // The subscription has lapsed. Every other row would be refused
        // for the same reason, so stop rather than burn through the queue
        // marking each one failed. Nothing is dropped: the records are
        // still here and still queued, and they go up when it is renewed.
        return { sent, failed, skipped: 'needs-plan', reason: err.message };
      }
      failed += 1;
      await db.modify<OutboxItem>('outbox', row.id, (current) =>
        current && current.stamp === row.stamp
          ? {
              ...current,
              attempts: current.attempts + 1,
              lastError: err instanceof Error ? err.message : String(err),
            }
          : undefined,
      );
    }
  }

  return { sent, failed, skipped: null };
}

interface CatchWithPhoto {
  id: string;
  photo?: Blob;
  [key: string]: unknown;
}

interface SyncableRecord {
  media?: Blob;
  visibility?: string;
  catches?: CatchWithPhoto[];
  [key: string]: unknown;
}

async function send(row: OutboxItem): Promise<void> {
  const path = `/api/${row.store}/${encodeURIComponent(row.recordId)}`;

  if (row.op === 'delete') {
    await api(path, { method: 'DELETE' });
    return;
  }

  const record = await db.get<SyncableRecord>(row.store, row.recordId);
  // Deleted locally before the queue got to it.
  if (!record) return;

  const { media, catches, ...rest } = record;

  // Blobs go as files, so strip them out of the JSON body. The server
  // matches each one back to its fish by id.
  const catchPhotos: { id: string; photo: Blob }[] = [];
  const plainCatches = catches?.map((entry) => {
    const { photo, photoType: _photoType, ...withoutPhoto } = entry;
    if (photo instanceof Blob) catchPhotos.push({ id: entry.id, photo });
    return withoutPhoto;
  });

  const json = {
    ...rest,
    ...(plainCatches ? { catches: plainCatches } : {}),
  };

  const hasFiles = media instanceof Blob || catchPhotos.length > 0;
  if (!hasFiles) {
    try {
      await api(path, { method: 'PUT', body: JSON.stringify(json) });
    } catch (err) {
      // Same as the multipart path below. Without this a written entry
      // edited on two devices failed forever instead of keeping both.
      if (err instanceof ApiError && err.status === 409) {
        await keepBoth(row, record);
        return;
      }
      throw err;
    }
    return;
  }

  const form = new FormData();
  form.append('record', JSON.stringify(json));
  if (media instanceof Blob) form.append('media', media, 'media');
  for (const { id, photo } of catchPhotos) {
    form.append(`media:${id}`, photo, `${id}.jpg`);
  }

  const res = await fetch(apiUrl(path), {
    method: 'PUT',
    body: form,
    credentials: 'include',
  });
  if (res.status === 409) {
    await keepBoth(row, record);
    return;
  }
  if (!res.ok) {
    // The server's own words, when it has any. A member who is over their
    // storage needs to be told that, not "HTTP 413".
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // not JSON; the status will do
    }
    throw new ApiError(res.status, message);
  }
}

/**
 * Resolves a conflict without losing anything.
 *
 * The server refused the write because a newer version exists, which means
 * this record was edited on two devices. Rather than pick a winner and
 * silently drop the other, the local copy is kept as a separate record and
 * the member is left to reconcile them. Losing somebody's trip because two
 * phones disagreed is not a trade worth making.
 */
async function keepBoth(row: OutboxItem, local: SyncableRecord): Promise<void> {
  const copyId = newId();
  const now = new Date().toISOString();
  await db.put(row.store, {
    ...local,
    id: copyId,
    createdAt: now,
    updatedAt: now,
    title:
      typeof local.title === 'string'
        ? // The Dockerfile checks for this wording to prove sync is on in
          // the production image. Change both together.
          `${local.title} (edited on another device)`
        : 'Conflicted copy',
    conflictOf: row.recordId,
  });
  // Queue the copy so the other device sees it too.
  await enqueue({ store: row.store, op: 'upsert', id: copyId });
}

/**
 * Keeps this device and the member's others in step. Call once at startup.
 *
 * Both directions: what was written here goes up, and whatever happened on
 * another device comes down. It runs when the signal comes back, when the
 * app comes back on screen, every two minutes while it is open and visible,
 * and, for uploads, shortly after each save.
 */
export function startSyncOnReconnect(): () => void {
  live = true;
  const visible = () => document.visibilityState === 'visible';

  const onOnline = () => {
    void exchange().catch(() => undefined);
  };
  // Back on screen after a while away: another device may have changed
  // something. Not on every flick between apps, though.
  const onVisible = () => {
    if (!visible() || !navigator.onLine) return;
    if (Date.now() - lastExchange < RECHECK_AFTER_MS) return;
    void exchange().catch(() => undefined);
  };
  // And while it sits open on a laptop. A pull with a cursor moves almost
  // nothing when nothing changed, so this is cheap. Never while hidden, so
  // a phone in a pocket does not spend its battery on it.
  const poll = setInterval(() => {
    if (visible() && navigator.onLine) void exchange().catch(() => undefined);
  }, CHECK_EVERY_MS);

  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);
  if (navigator.onLine) onOnline();
  return () => {
    live = false;
    clearTimeout(sendTimer);
    clearInterval(poll);
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
